import { z } from "zod";
import { PropertySchema } from "../../properties/response/ShowPropertiesResponse";

const RateSchema = z.object({
    date: z.string(),
    rate: z.coerce.number(),
});

const RoomTypeSchema = z.object({
    id: z.string(),
    property_id: z.string(),
    // Nullable: property deactivation nulls this server-side (DeactivatePropertyChannexJob).
    channex_id: z.string().nullable(),
    name: z.string(),
    description: z.nullable(z.string()).optional(),
    no_of_beds: z.number(),
    cut_of_days: z.number(),
    is_free_allotment: z.boolean(),
    is_auto_assign: z.boolean(),
    is_dynamic_pricing: z.boolean(),
    total_rooms: z.number(),
    created_at: z.string(),
    updated_at: z.string(),
    occ_adults: z.number(),
    occ_children: z.number(),
    occ_infants: z.number(),
    default_occupancy: z.number(),
    room_kind: z.string(),
    capacity: z.number(),
});

const RoomSchema = z.object({
    id: z.string(),
    property_id: z.string(),
    room_type_id: z.string(),
    room_number: z.string(),
    status: z.string(),
    condition: z.string(),
    created_at: z.string(),
    updated_at: z.string(),
});

const RatePlanSchema = z.object({
    id: z.string(),
    room_type_id: z.string(),
    property_id: z.string(),
    // Nullable: property deactivation nulls this server-side (DeactivatePropertyChannexJob).
    channex_id: z.string().nullable(),
    name: z.string(),
    description: z.nullable(z.string()).optional(),
    max_night: z.number(),
    min_night: z.number(),
    guest_include: z.number(),
    guest_max: z.number(),
    extra_adult: z.number(),
    extra_child: z.number(),
    extra_adult_rate: z.string(),
    extra_child_rate: z.string(),
    rate: z.string(),
    min_rate: z.string(),
    start_date: z.nullable(z.string()),
    end_date: z.nullable(z.string()),
    parent_id: z.nullable(z.string()).optional(),
    specific_rule: z.string(),
    specific_amount: z.string(),
    specific_percent: z.number(),
    specific_amount_type: z.string(),
    created_at: z.string(),
    updated_at: z.string(),
});

const DataSchema = z.object({
    id: z.string(),
    property_id: z.string(),
    customer_name: z.string(),
    customer_surname: z.nullable(z.string()),
    customer_email: z.nullable(z.string()),
    customer_phone: z.nullable(z.string()),
    customer_address: z.nullable(z.string()),
    customer_country: z.nullable(z.string()),
    customer_state: z.nullable(z.string()),
    customer_city: z.nullable(z.string()),
    customer_zip: z.nullable(z.string()),
    customer_date_of_birth: z.nullable(z.string()),
    customer_identity_number: z.nullable(z.string()),
    invoice_number: z.string(),
    voucher_number: z.nullable(z.string()).optional(),
    invoice_date: z.string(),
    status: z.nullable(z.string()).optional(),
    mark: z.nullable(z.string()).optional(),
    night: z.nullable(z.number()).optional(),
    payment_type: z.nullable(z.string()).optional(),
    room_type_id: z.nullable(z.string()),
    room_id: z.nullable(z.string()),
    rate_plan_id: z.nullable(z.string()),
    rates: z.array(RateSchema),
    total_rate: z.string(),
    checkin_date: z.string(),
    checkout_date: z.string(),
    adults: z.number(),
    children: z.number(),
    total_guests: z.number(),
    public_note: z.nullable(z.string()),
    private_note: z.nullable(z.string()),
    // Menandai booking hasil bulk-import di channel manager (bukan reservasi
    // normal). Sengaja optional supaya FE tetap lolos validasi terhadap backend
    // versi lama — tanpa ini seluruh popover/modal detail booking ikut gagal
    // render kalau backend belum ter-deploy.
    is_imported: z.boolean().nullish(),
    // Penanda hasil split reservasi (lihat booking_rooms.split_date /
    // parent_booking_room_id di backend). `.optional()` supaya FE tetap lolos
    // validasi terhadap backend versi lama yang belum mengekspos field ini.
    split_date: z.string().nullable().optional(),
    parent_booking_room_id: z.string().nullable().optional(),
    split_role: z.enum(['segment_1', 'segment_2']).nullable().optional(),
    source: z.string(),
    property: PropertySchema.optional(),
    room_type: z.nullable(RoomTypeSchema).optional(),
    room: z.nullable(RoomSchema).optional(),
    rate_plan: z.nullable(RatePlanSchema).optional(),
    agent_id: z.nullable(z.string()),
    logo_url: z.nullable(z.string()).optional(),
    ota_name: z.nullable(z.string()).optional(),
    agent_name: z.nullable(z.string()).optional(),
    created_at: z.nullable(z.string()),
    system_id: z.nullable(z.string()).optional(),
    down_payment: z.nullable(z.coerce.string()).optional(),
    due_payment: z.nullable(z.coerce.string()).optional(),
    total_payments: z.nullable(z.coerce.string()).optional(),
    total_payment_amount: z.nullable(z.coerce.string()).optional(),
    total_surcharge_amount: z.nullable(z.coerce.string()).optional(),
    total_extra: z.nullable(z.coerce.string()).optional(),
    total_extras_amount: z.nullable(z.coerce.string()).optional(),
    total_with_extras: z.nullable(z.coerce.string()).optional(),
    // Ada minimal 1 extra AKTIF (bukan hasil POS yang sudah dibatalkan) --
    // KHUSUS untuk indikator badge tombol "+ Extra", beda dari total_extra*
    // di atas yang tetap menjumlahkan semua extra termasuk yang dibatalkan
    // (dipakai kalkulasi tagihan, tidak berubah). `.nullish()` supaya FE
    // tetap lolos validasi terhadap backend versi lama yang belum kirim field ini.
    has_extra: z.boolean().nullish(),
    // Jumlah item extra AKTIF (predikat sama persis dengan has_extra) --
    // dipakai hover tooltip kalender untuk menampilkan jumlah item, bukan
    // cuma flag boolean. `.nullish()` dengan alasan yang sama seperti
    // has_extra di atas.
    extra_count: z.number().nullish(),
    payments: z.nullable(z.array(z.object({
        id: z.string(),
        amount: z.string(),
        booking_id: z.string(),
        booking_room_id: z.string(),
        created_at: z.string(),
        date_payment: z.string(),
        payment_type: z.string(),
        property_id: z.string(),
        surcharge: z.string(),
        updated_at: z.string(),
        user_id: z.nullable(z.string()),
    }))).optional(),
});

const ShowBookingResponseSchema = z.object({
    success: z.boolean(),
    message: z.string(),
    code: z.number(),
    data: DataSchema,
});

export default ShowBookingResponseSchema;
export { DataSchema as BookingSchema };

export type ShowBookingResponseType = z.infer<typeof ShowBookingResponseSchema>;
export type BookingType = z.infer<typeof DataSchema>;